import { useState } from "react";
import { Layout, Menu, Avatar, Dropdown, Typography, Space, Button } from "antd";
import {
  DashboardOutlined,
  BookOutlined,
  TeamOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  SafetyOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  RocketOutlined,
  CalendarOutlined,
  ApartmentOutlined,
  EditOutlined,
  DollarOutlined,
  UsergroupAddOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { PermissionGate } from "./PermissionGate";
import { hasAction } from "./usePermissions";
import type { AdminAction } from "./types";

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

interface MenuEntry {
  key: string;
  label: string;
  icon: React.ReactNode;
  perm?: AdminAction;
}

/**
 * AdminLayout — sider + topbar shell cho /admin/*.
 *
 * Sprint 8A B1: dùng AntD Layout thuần. Add @ant-design/pro-layout nếu cần
 * multi-tab / breadcrumb sâu hơn ở B2+.
 *
 * Menu items ẩn theo permission (L3 RBAC). Toàn bộ /admin/* đã qua AdminRouteGuard
 * (L2). BE permission_classes là L1.
 */
export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Mỗi entry có optional `perm` — ẩn nếu user không có. Dashboard luôn show.
  const menuEntries: MenuEntry[] = [
    { key: "/admin", label: "Tổng quan", icon: <DashboardOutlined /> },
    { key: "/admin/courses", label: "Môn học", icon: <BookOutlined />, perm: "course.edit" },
    {
      // Sprint 25 B5 — AI Lesson Designer, chuyển từ /teach/designer vào admin
      // (2026-07-13) — cùng perm với "Môn học" vì đây là công cụ soạn khoá bằng
      // AI, backend gate bởi `IsAuthor` (instructor/tenant_admin/superadmin),
      // khớp đúng role set của "course.edit".
      key: "/admin/designer",
      label: "AI Lesson Designer",
      icon: <RobotOutlined />,
      perm: "course.edit",
    },
    {
      key: "/admin/curriculum",
      label: "Chương trình đào tạo",
      icon: <ApartmentOutlined />,
      perm: "curriculum.manage",
    },
    { key: "/admin/users", label: "Người dùng", icon: <TeamOutlined />, perm: "user.edit" },
    {
      key: "/admin/users/bulk-import",
      label: "Gán ngành hàng loạt",
      icon: <UsergroupAddOutlined />,
      perm: "user.bulk_import",
    },
    {
      key: "/admin/term-offerings",
      label: "Kỳ học",
      icon: <CalendarOutlined />,
      perm: "term_offering.manage",
    },
    {
      key: "/admin/tuition-rates",
      label: "Học phí/tín chỉ",
      icon: <DollarOutlined />,
      perm: "tuition_rate.manage",
    },
    {
      key: "/admin/grading",
      label: "Chấm bài",
      icon: <EditOutlined />,
      perm: "answer.grade",
    },
    {
      key: "/admin/analytics",
      label: "Phân tích",
      icon: <BarChartOutlined />,
      perm: "analytics.read",
    },
    {
      key: "/admin/orders",
      label: "Đơn hàng",
      icon: <ShoppingOutlined />,
      perm: "order.read",
    },
    {
      key: "/admin/proctor",
      label: "Giám sát thi",
      icon: <SafetyOutlined />,
      // Sprint 6 đã có — share existing route
    },
    {
      key: "/admin/audit",
      label: "Nhật ký",
      icon: <FileTextOutlined />,
      perm: "audit.read",
    },
    // Sprint 24 B5 — Career Connect admin (interest list + status PATCH + CSV export)
    {
      key: "/admin/careers/interests",
      label: "Việc làm",
      icon: <RocketOutlined />,
      perm: "audit.read",
    },
  ];

  // Build menu items applying permission filter — use `usePermissions` directly để khỏi
  // wrap mỗi item trong PermissionGate (Menu items không support fragment children).
  const visibleEntries = menuEntries.filter((entry) => {
    if (!entry.perm) return true;
    return hasAction(user, entry.perm);
  });

  const menuItems = visibleEntries.map((entry) => ({
    key: entry.key,
    icon: entry.icon,
    label: <Link to={entry.key}>{entry.label}</Link>,
  }));

  // selectedKeys: longest matching prefix → highlight đúng khi vào sub-route
  const selectedKey =
    visibleEntries
      .map((e) => e.key)
      .filter((k) => location.pathname === k || location.pathname.startsWith(k + "/"))
      .sort((a, b) => b.length - a.length)[0] ?? "/admin";

  const userMenuItems = [
    {
      key: "back-app",
      label: "Quay về trang chính",
      icon: <HomeOutlined />,
      onClick: () => navigate("/"),
    },
    { type: "divider" as const },
    {
      key: "logout",
      label: "Đăng xuất",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        theme="dark"
        width={220}
      >
        <div
          style={{
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: 1,
          }}
        >
          {collapsed ? "E" : "E-learning 2026 Admin"}
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={[selectedKey]} items={menuItems} />
      </Sider>
      <Layout>
        <Header
          style={{
            background: "#fff",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16 }}
          />
          <div style={{ flex: 1 }} />
          {/* Slot cho future "Bạn đang admin tenant X" badge */}
          <PermissionGate perm="audit.read">
            <Text type="secondary" style={{ marginRight: 16 }}>
              Quản trị viên
            </Text>
          </PermissionGate>
          {user && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: "pointer" }}>
                <Avatar icon={<UserOutlined />} />
                <Text>{user.email}</Text>
              </Space>
            </Dropdown>
          )}
        </Header>
        <Content style={{ padding: 24, background: "#f4f5f7" }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
